import {
  applyHref,
  applySrc,
  applyPlaceholder,
  applyAlt,
  applyCustomAttribute,
  getObjectKeys,
  applyDisabled,
  booleanAttributeToBoolean,
  applyCustomStyleProperty,
} from "./utils.js";
import { renderAuthMarkup } from "./html.js";

/**
 * @typedef {HTMLElement & {
 *   busy: boolean,
 *   readonly popupMode: boolean,
 *   setUsername: (username: string) => void,
 *   focusUsername: () => void,
 *   clear: () => void,
 *   setErrors: (messages: string[]) => void,
 *   clearErrors: () => void,
 *   openPopup: () => void,
 *   closePopup: () => void,
 * }} HygienaAuthElementApi
 */
/** @typedef {SubmitEvent} HygienaSubmitEvent */
/** @typedef {{ form: string }} HygienaAuthErrors */
/** @typedef {{ source: "user" | "method" }} HygienaAuthPopupClosedEvent */
/** @typedef {(typeof HygienaAuthElement.observedAttributes)[number]} HygienaAuthAttribute */
/** @typedef {(typeof HygienaAuthElement.SLOT_IMAGES)[number]} HygienaAuthSlotImage */
/** @typedef {(typeof HygienaAuthElement.SLOT_LABELS)[number]} HygienaAuthSlotLabel */

/** @implements {HygienaAuthElementApi} */
export class HygienaAuthElement extends HTMLElement {
  static TAG_NAME = "hygiena-auth";

  static EVENT_TYPES = {
    authSubmit: "auth-submit",
    popupOpened: "popup-opened",
    popupClosed: "popup-closed",
  };

  /** @type {readonly ["logo", "hygiena-logo"]} */
  static SLOT_IMAGES = ["logo", "hygiena-logo"];

  /** @type {readonly ["forgot-password-label", "submit-label", "register-prompt-label", "register-label", "copyright-label", "close-label"]} */
  static SLOT_LABELS = [
    "forgot-password-label",
    "submit-label",
    "register-prompt-label",
    "register-label",
    "copyright-label",
    "close-label",
  ];

  static SELECTORS = {
    form: "[data-role='popup-dialog'] [data-role='auth-form']",
    usernameInput: "[data-input='username']",
    passwordInput: "[data-input='password']",
    fieldset: ".hauth-fieldset",
    popupDialog: "[data-role='popup-dialog']",
    popupCloseButton: "[data-role='popup-close-button']",
    submitButton: "#loginButton",
    formError: "[data-role='form-error']",
    forgotPasswordLinks:
      "[data-role='forgot-password-link'], #forgotPasswordLink",
    registerLinks: "[data-role='register-link']",
    copyrightLinks: "[data-role='copyright-link']",
    logo: "[data-role='logo']",
    hygienaLogos: "[data-role='hygiena-logo']",
  };

  static STATIC_BINDINGS = {
    mode: {
      default: "embedded",
      apply: {
        [HygienaAuthElement.SELECTORS.popupDialog]: applyCustomAttribute.bind(
          null,
          "data-mode",
        ),
      },
    },
    busy: {
      default: false,
      apply: {
        [HygienaAuthElement.SELECTORS.form]: applyCustomAttribute.bind(
          null,
          "aria-busy",
        ),
        [HygienaAuthElement.SELECTORS.popupDialog]: applyCustomAttribute.bind(
          null,
          "data-busy",
        ),
        [HygienaAuthElement.SELECTORS.fieldset]: applyDisabled.bind(null),
      },
    },
    "forgot-password-href": {
      default: "https://suretrend.hygiena.com/ForgotPassword",
      apply: { [HygienaAuthElement.SELECTORS.forgotPasswordLinks]: applyHref },
    },
    "register-href": {
      default: "https://suretrend.hygiena.com/Register",
      apply: { [HygienaAuthElement.SELECTORS.registerLinks]: applyHref },
    },
    "copyright-href": {
      default:
        "https://sureTrendet.hygiena.com/privacypolicy/Hygiena-STC-Privacy-Policy.pdf",
      apply: { [HygienaAuthElement.SELECTORS.copyrightLinks]: applyHref },
    },
    "logo-src": {
      default: new URL("./assets/logo.svg", import.meta.url).href,
      apply: { [HygienaAuthElement.SELECTORS.logo]: applySrc },
    },
    "background-img-src": {
      default: new URL("./assets/background.svg", import.meta.url).href,
      apply: {
        [HygienaAuthElement.SELECTORS.popupDialog]: (
          /** @type {string} */ value,
          /** @type {HTMLElement} */ el,
        ) =>
          applyCustomStyleProperty(
            "--auth-backdrop-image",
            `url("${value}")`,
            el,
          ),
      },
    },
    "hygiena-logo-src": {
      default: new URL("./assets/hygienaLogo.svg", import.meta.url).href,
      apply: { [HygienaAuthElement.SELECTORS.hygienaLogos]: applySrc },
    },
    "username-placeholder": {
      default: "Username",
      apply: { [HygienaAuthElement.SELECTORS.usernameInput]: applyPlaceholder },
    },
    "password-placeholder": {
      default: "Password",
      apply: { [HygienaAuthElement.SELECTORS.passwordInput]: applyPlaceholder },
    },
    "logo-alt": {
      default: "Logo",
      apply: { [HygienaAuthElement.SELECTORS.logo]: applyAlt },
    },
    "required-fields-error": {
      default: "Username and password are required.",
      apply: {},
    },
  };

  static observedAttributes = getObjectKeys(HygienaAuthElement.STATIC_BINDINGS);

  /** @type {ShadowRoot} */
  #shadowRoot;
  #popupOpen = false;

  constructor() {
    super();
    this.#shadowRoot = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.#render();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) {
      return;
    }

    const binding =
      HygienaAuthElement.STATIC_BINDINGS[
        /** @type {HygienaAuthAttribute} */ (name)
      ];
    if (!binding) return;
    this.#applyAttributeBinding(binding, newValue);

    if (name === "busy") {
      this.#syncSubmitButtonState();
    }

    if (name === "mode") {
      this.#popupOpen = false;
      this.#syncPopupUi();
      this.#syncSubmitButtonState();
    }
  }

  /** @returns {boolean} */
  get busy() {
    return booleanAttributeToBoolean(
      this.#getAttributeValue("busy"),
      HygienaAuthElement.STATIC_BINDINGS.busy.default,
    );
  }

  /** @param {boolean} value */
  set busy(value) {
    this.toggleAttribute("busy", value);
  }

  /** @param {string} value */
  setUsername(value) {
    this.#applyToElement(
      HygienaAuthElement.SELECTORS.usernameInput,
      (/** @type {HTMLInputElement} */ e) => (e.value = value),
    );
  }

  focusUsername() {
    if (this.popupMode && !this.#popupOpen) {
      this.openPopup();
    }
    this.#getElement(HygienaAuthElement.SELECTORS.usernameInput)?.focus();
  }

  clear() {
    this.#applyToElement(
      HygienaAuthElement.SELECTORS.usernameInput,
      (/** @type {HTMLInputElement} */ e) => (e.value = ""),
    );
    this.#applyToElement(
      HygienaAuthElement.SELECTORS.passwordInput,
      (/** @type {HTMLInputElement} */ e) => (e.value = ""),
    );
    this.clearErrors();
    this.#syncSubmitButtonState();
  }

  openPopup() {
    if (!this.popupMode) {
      return;
    }

    const wasClosed = !this.#popupOpen;
    this.#popupOpen = true;
    this.#syncPopupUi();
    this.#syncSubmitButtonState();
    if (wasClosed) {
      this.#dispatchPopupOpen();
    }
    this.focusUsername();
  }

  closePopup() {
    this.#closePopupInternal("method");
  }

  /** @param {string[]} value */
  setErrors(value) {
    this.#error = value.join("\n");
  }

  clearErrors() {
    this.#error = "";
  }

  /** @returns {boolean} */
  get popupMode() {
    return this.#getAttributeValue("mode") === "popup";
  }

  /** @returns {string | undefined} */
  get #error() {
    const errorText = this.#getElement(
      HygienaAuthElement.SELECTORS.formError,
    )?.textContent?.trim();
    if (!errorText?.length) return;
    return errorText;
  }

  /** @param {string} value */
  set #error(value) {
    this.#applyToElement(
      HygienaAuthElement.SELECTORS.formError,
      (/** @type {HTMLElement} */ e) => {
        e.textContent = value.trim();
        e.hidden = !value.trim().length;
      },
    );
  }

  #syncPopupUi() {
    const dialog = /** @type {HTMLDialogElement | null} */ (
      this.#getElement(HygienaAuthElement.SELECTORS.popupDialog)
    );
    const popupCloseButton = this.#getElement(
      HygienaAuthElement.SELECTORS.popupCloseButton,
    );

    if (!dialog) {
      return;
    }

    if (!this.popupMode) {
      popupCloseButton?.setAttribute("hidden", "");
      dialog.hidden = false;

      if (!dialog.open) {
        dialog.show();
        return;
      }

      if (dialog.matches(":modal")) {
        dialog.close();
        dialog.show();
      }

      return;
    }

    popupCloseButton?.removeAttribute("hidden");

    if (this.#popupOpen) {
      dialog.hidden = false;

      if (dialog.open && !dialog.matches(":modal")) {
        dialog.close();
      }

      if (!dialog.open) {
        dialog.showModal();
      }

      return;
    }

    if (dialog.open) {
      dialog.close();
    }

    dialog.hidden = true;
  }

  /** @returns {boolean} */
  #validateCredentials() {
    if (!this.#hasRequiredCredentials) {
      this.#error = this.#getAttributeValue("required-fields-error");
      return false;
    }

    this.clearErrors();

    return true;
  }

  #syncSubmitButtonState() {
    const submitButton = /** @type {HTMLButtonElement | null} */ (
      this.#getElement(HygienaAuthElement.SELECTORS.submitButton)
    );
    if (!submitButton) {
      return;
    }

    submitButton.disabled = !this.#canSubmit;
  }

  /** @param {HygienaAuthPopupClosedEvent["source"]} source */
  #closePopupInternal(source) {
    if (!this.popupMode) {
      return;
    }

    this.#popupOpen = false;
    this.#syncPopupUi();
    this.#syncSubmitButtonState();
    this.#dispatchPopClose(source);
  }

  #dispatchPopupOpen() {
    this.dispatchEvent(
      new CustomEvent(HygienaAuthElement.EVENT_TYPES.popupOpened, {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** @param {HygienaAuthPopupClosedEvent["source"]} source */
  #dispatchPopClose(source) {
    /** @type {HygienaAuthPopupClosedEvent} */
    const detail = {
      source,
    };

    this.dispatchEvent(
      new CustomEvent(HygienaAuthElement.EVENT_TYPES.popupClosed, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** @param {HygienaSubmitEvent} detail */
  #dispatchAuthSubmit(detail) {
    this.dispatchEvent(
      new CustomEvent(HygienaAuthElement.EVENT_TYPES.authSubmit, {
        detail,
        bubbles: true,
        composed: true,
        cancelable: true,
      }),
    );
  }

  /** @param {Event} event */
  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    const field = target.dataset.input;
    if (field !== "username" && field !== "password") {
      return;
    }

    this.clearErrors();

    this.#syncSubmitButtonState();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    event.preventDefault();
    if (!this.#validateCredentials()) {
      return;
    }

    this.clearErrors();
    this.#dispatchAuthSubmit(event);
  };

  #handlePopupClose = () => {
    this.#closePopupInternal("user");
  };

  #render() {
    this.#shadowRoot.innerHTML = renderAuthMarkup();
    this.#bindShadowEvents();
    this.#syncStaticContent();
    this.#syncPopupUi();
    this.#syncSubmitButtonState();
  }

  #syncStaticContent() {
    for (const [attribute, binding] of Object.entries(
      HygienaAuthElement.STATIC_BINDINGS,
    )) {
      const value = this.#getAttributeValue(
        /** @type {HygienaAuthAttribute} */ (attribute),
      );
      this.#applyAttributeBinding(binding, value);
    }
  }

  #bindShadowEvents() {
    const form = /** @type {HTMLFormElement | null} */ (
      this.#getElement(HygienaAuthElement.SELECTORS.form)
    );
    const popupCloseButton = this.#getElement(
      HygienaAuthElement.SELECTORS.popupCloseButton,
    );
    const popupDialog = this.#getElement(
      HygienaAuthElement.SELECTORS.popupDialog,
    );

    this.#getElement(
      HygienaAuthElement.SELECTORS.usernameInput,
    )?.addEventListener("input", this.#handleInput);
    this.#getElement(
      HygienaAuthElement.SELECTORS.passwordInput,
    )?.addEventListener("input", this.#handleInput);

    form?.addEventListener("submit", this.#handleSubmit);
    popupCloseButton?.addEventListener("click", this.#handlePopupClose);

    let startedOnBackdrop = false;
    popupDialog?.addEventListener(
      "mousedown",
      (e) => (startedOnBackdrop = e.target === popupDialog),
    );
    popupDialog?.addEventListener("click", (e) => {
      const endedOnBackdrop = e.target === popupDialog;

      // If they released outside, but started inside, KILL the event
      if (!startedOnBackdrop && endedOnBackdrop) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      if (endedOnBackdrop) {
        this.#closePopupInternal("user");
      }
    });
  }

  /**
   * @template {HygienaAuthAttribute} K
   * @param {K} name
   */
  #getAttributeValue(name) {
    return /** @type {string} */ (this.getAttribute(name));
  }

  /** @returns {boolean} */
  get #hasRequiredCredentials() {
    const usernameInput = /** @type {HTMLInputElement | null} */ (
      this.#getElement(HygienaAuthElement.SELECTORS.usernameInput)
    );
    const passwordInput = /** @type {HTMLInputElement | null} */ (
      this.#getElement(HygienaAuthElement.SELECTORS.passwordInput)
    );
    const hasUsername = Boolean(usernameInput?.value.trim());
    const hasPassword = Boolean(passwordInput?.value);
    return hasUsername && hasPassword;
  }

  /** @returns {boolean} */
  get #canSubmit() {
    if (this.popupMode && !this.#popupOpen) {
      return false;
    }

    return this.#hasRequiredCredentials && !this.busy;
  }

  /**
   * @param {(typeof HygienaAuthElement.STATIC_BINDINGS)[HygienaAuthAttribute]} binding
   * @param {string | null} [newValue]
   */
  #applyAttributeBinding(binding, newValue) {
    const value =
      typeof binding.default === "boolean"
        ? booleanAttributeToBoolean(newValue, binding.default).toString()
        : newValue || binding.default;

    Object.entries(binding.apply).forEach(([selector, applyFn]) => {
      this.#applyToElement(selector, applyFn.bind(null, value || ""));
    });
  }

  /**
   * @template {HTMLElement} T
   * @param {string} selector
   * @returns {T | null}
   */
  #getElement(selector) {
    return this.#shadowRoot.querySelector(selector);
  }

  /**
   * @template {HTMLElement} T
   * @param {string} selector
   * @returns {NodeListOf<T>}
   */
  #getElements(selector) {
    return this.#shadowRoot.querySelectorAll(selector);
  }

  /**
   * @template {HTMLElement} T
   * @param {string} selector
   * @param {(element: T) => void} callback
   * @returns {T[]}
   */
  #applyToElement(selector, callback) {
    const elements = this.#getElements(selector);
    elements.forEach(callback);
    return Array.from(elements);
  }
}
